// ============================================================================
// TMOS FRONTEND STATUS ENUM TYPES - COMPLETE ARCHITECTURAL FOUNDATION
// ============================================================================
// This file defines all status enum types for the frontend
// ensuring consistency with backend types and frontend/backend alignment

// ============================================================================
// RBAC ENUMS (Matching Backend)
// ============================================================================

// User Role Types - Database-driven (no enum restrictions)
// Roles are stored in the unified roles table and can be dynamically created
export type UserRole = string; // Allows any role name from database

// Navigation Modules (Database-driven, no duplicates)
export enum NavigationModule {
  // Administration (Consolidated)
  USER_MANAGEMENT = 'USER_MANAGEMENT',
  ACCESS_MANAGEMENT = 'ACCESS_MANAGEMENT',
  SYSTEM_ADMINISTRATION = 'SYSTEM_ADMINISTRATION',
  
  // Business Management (Consolidated)
  BUSINESS_MANAGEMENT = 'BUSINESS_MANAGEMENT',
  TRANSFORMATION_MANAGEMENT = 'TRANSFORMATION_MANAGEMENT',
  KPI_MANAGEMENT = 'KPI_MANAGEMENT',
  PROCESS_MODELING = 'PROCESS_MODELING',
  
  // System Features
  DASHBOARD = 'DASHBOARD',
  CHANGE_MANAGEMENT = 'CHANGE_MANAGEMENT',
  DATA_IMPORT = 'DATA_IMPORT',
  DATA_CONNECTION = 'DATA_CONNECTION',
  
  // Reporting (Consolidated)
  REPORTING_ANALYTICS = 'REPORTING_ANALYTICS',
  
  // Additional Features
  PARTNER_MANAGEMENT = 'PARTNER_MANAGEMENT',
  TEMPLATES_BEST_PRACTICES = 'TEMPLATES_BEST_PRACTICES'
}

// Permission Types
export enum PermissionType {
  READ = 'READ',
  WRITE = 'WRITE',
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
  EXECUTE = 'EXECUTE',
  APPROVE = 'APPROVE',
  REJECT = 'REJECT',
  ASSIGN = 'ASSIGN',
  REVOKE = 'REVOKE',
  EXPORT = 'EXPORT',
  IMPORT = 'IMPORT',
  ADMINISTER = 'ADMINISTER',
  ADMIN = 'ADMIN'
}

// Core Entity Status (Most Common)
export type EntityStatus = 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';

// Verification Status
export type VerificationStatus = 'PENDING' | 'VERIFIED' | 'FAILED';

// Invitation Status
export type InvitationStatus = 'PENDING' | 'ACCEPTED' | 'EXPIRED' | 'CANCELLED';

// Transformation Status
export type TransformationStatus = 'PLANNING' | 'ACTIVE' | 'ON_HOLD' | 'COMPLETED' | 'CANCELLED';

// Milestone Status
export type MilestoneStatus = 'PLANNED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';

// KPI Performance Status
export type KpiPerformanceStatus = 'ABOVE_TARGET' | 'ON_TARGET' | 'BELOW_TARGET';

// Platform Service Status
export type PlatformStatus = 'ACTIVE' | 'MAINTENANCE' | 'DEPRECATED';

// System Status (Extended)
export type SystemStatus = 'ACTIVE' | 'INACTIVE' | 'DEPRECATED';

// Process Status (Extended)
export type ProcessStatus = 'ACTIVE' | 'INACTIVE' | 'MAINTENANCE' | 'RETIRED';

// Integration Status (Extended)
export type IntegrationStatus = 'ACTIVE' | 'INACTIVE' | 'DISCONTINUED';

// API Status (Extended)
export type ApiStatus = 'ACTIVE' | 'INACTIVE' | 'ERROR';

// Tenant Status (Extended)
export type TenantStatus = 'PENDING' | 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';

// User Status (Extended)
export type UserStatus = 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';

// Partner Status
export type PartnerStatus = 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';

// ============================================================================
// ENUM CONSTANTS FOR FRONTEND COMPONENTS
// ============================================================================

export const ENTITY_STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'SUSPENDED', label: 'Suspended' }
];

export const VERIFICATION_STATUS_OPTIONS = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'VERIFIED', label: 'Verified' },
  { value: 'FAILED', label: 'Failed' }
];

export const INVITATION_STATUS_OPTIONS = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'ACCEPTED', label: 'Accepted' },
  { value: 'EXPIRED', label: 'Expired' },
  { value: 'CANCELLED', label: 'Cancelled' }
];

export const TRANSFORMATION_STATUS_OPTIONS = [
  { value: 'PLANNING', label: 'Planning' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'ON_HOLD', label: 'On Hold' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' }
];

export const MILESTONE_STATUS_OPTIONS = [
  { value: 'PLANNED', label: 'Planned' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' }
];

export const KPI_PERFORMANCE_OPTIONS = [
  { value: 'ABOVE_TARGET', label: 'Above Target' },
  { value: 'ON_TARGET', label: 'On Target' },
  { value: 'BELOW_TARGET', label: 'Below Target' }
];

export const PLATFORM_STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'MAINTENANCE', label: 'Maintenance' },
  { value: 'DEPRECATED', label: 'Deprecated' }
];

export const SYSTEM_STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'DEPRECATED', label: 'Deprecated' }
];

export const PROCESS_STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'MAINTENANCE', label: 'Maintenance' },
  { value: 'RETIRED', label: 'Retired' }
];

export const INTEGRATION_STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'DISCONTINUED', label: 'Discontinued' }
];

export const API_STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'ERROR', label: 'Error' }
];

export const TENANT_STATUS_OPTIONS = [
  { value: 'PENDING', label: 'tenantManagement.statusPending' },
  { value: 'ACTIVE', label: 'tenantManagement.statusActive' },
  { value: 'INACTIVE', label: 'tenantManagement.statusInactive' },
  { value: 'SUSPENDED', label: 'tenantManagement.statusSuspended' }
];

export const USER_STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'SUSPENDED', label: 'Suspended' }
];

export const PARTNER_STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'SUSPENDED', label: 'Suspended' }
];

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

export const isValidEntityStatus = (status: string): status is EntityStatus => {
  return ['ACTIVE', 'INACTIVE', 'SUSPENDED'].includes(status);
};

export const isValidTransformationStatus = (status: string): status is TransformationStatus => {
  return ['PLANNING', 'ACTIVE', 'ON_HOLD', 'COMPLETED', 'CANCELLED'].includes(status);
};

export const isValidKpiPerformanceStatus = (status: string): status is KpiPerformanceStatus => {
  return ['ABOVE_TARGET', 'ON_TARGET', 'BELOW_TARGET'].includes(status);
};

export const getStatusLabel = (status: string, options: readonly { value: string; label: string }[]): string => {
  const option = options.find(opt => opt.value === status);
  return option?.label || status;
};

export const getStatusColor = (status: string): string => {
  switch (status) {
    case 'ACTIVE':
    case 'VERIFIED':
    case 'COMPLETED':
    case 'ON_TARGET':
      return 'green';
    case 'PENDING':
    case 'PLANNING':
    case 'PLANNED':
      return 'yellow';
    case 'INACTIVE':
    case 'SUSPENDED':
    case 'CANCELLED':
    case 'FAILED':
    case 'BELOW_TARGET':
      return 'red';
    case 'ON_HOLD':
    case 'IN_PROGRESS':
      return 'blue';
    case 'MAINTENANCE':
    case 'DEPRECATED':
    case 'RETIRED':
    case 'DISCONTINUED':
      return 'gray';
    default:
      return 'gray';
  }
};

// ============================================================================
// DEFAULT VALUES
// ============================================================================

export const DEFAULT_STATUS_VALUES = {
  ENTITY: 'ACTIVE' as EntityStatus,
  VERIFICATION: 'PENDING' as VerificationStatus,
  INVITATION: 'PENDING' as InvitationStatus,
  TRANSFORMATION: 'PLANNING' as TransformationStatus,
  MILESTONE: 'PLANNED' as MilestoneStatus,
  KPI_PERFORMANCE: 'ON_TARGET' as KpiPerformanceStatus,
  PLATFORM: 'ACTIVE' as PlatformStatus,
  SYSTEM: 'ACTIVE' as SystemStatus,
  PROCESS: 'ACTIVE' as ProcessStatus,
  INTEGRATION: 'ACTIVE' as IntegrationStatus,
  API: 'ACTIVE' as ApiStatus,
  TENANT: 'ACTIVE' as TenantStatus,
  USER: 'ACTIVE' as UserStatus,
  PARTNER: 'ACTIVE' as PartnerStatus
} as const;
